import React from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import { add } from 'date-fns'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import eventEmitter, { types } from '../../shared/eventEmitter'

import ConceptProgress from '../ConceptProgress'
import ContactCheck from './ContactCheck'

import styles from './style.module.scss'
import { TApi, TNotifications, TRoutes, TNews, TRoles, DOCUMENT_TYPES } from '../../shared/const'
import i18n from 'i18next'
import { ReactComponent as AlertIcon } from '../../assets/icons/notify-alert.svg'
import { ReactComponent as BellIcon } from '../../assets/icons/notify-bell.svg'
import { ReactComponent as SuccessIcon } from '../../assets/icons/notify-success.svg'
import { ReactComponent as AwardIcon } from '../../assets/icons/notify-award.svg'
import { ReactComponent as InfoIcon } from '../../assets/icons/notify-info.svg'
import { ReactComponent as NewsIcon } from '../../assets/icons/notify-news.svg'
import { ReactComponent as EventIcon } from '../../assets/icons/notify-event.svg'
import http from '../../shared/http'
import Btn from '../Btn'
import { updateCompanyInfo } from '../../redux/ducks/user'
import { optionLabel } from '../../shared/optionList'
import { openApprovalRequirements } from '../../shared/approvalDialog'
import { formatVienna } from '../../shared/datetime'
import { maybeShowSupporterInvitation } from '../../shared/supporterInvitation'

/**
 * Die zwei Saetze zum Stand der Lernstrecke.
 *
 * Beide Mitteilungen zum Abschluss (an die Person und an die Lehrkraft) nennen
 * dieselben Zahlen — sie kommen aus derselben `info` und werden hier einmal
 * formuliert. Zwei Fassungen davon hiessen: Schueler:in und Lehrkraft lesen
 * verschiedene Staende derselben Junior Company.
 *
 * **Ohne Mehrzahlformen von i18next.** Die eingesetzte Fassung (20) kennt
 * `_one`/`_other` noch nicht; „1 Personen" waere die Folge. Deshalb drei
 * eigene Schluessel statt einer Mehrzahlregel.
 */
const kursStandSaetze = info => {
	const noetig = info?.courseRequired
	const erfuellt = info?.courseDone
	const offen = info?.openCount

	const stand =
		typeof noetig === 'number' && typeof erfuellt === 'number'
			? i18n.t('{{val1}} of {{val2}} required completions are done.', { val1: erfuellt, val2: noetig })
			: null

	let rest = null
	if (offen === 0) rest = i18n.t('Everyone in the Junior Company has finished.')
	else if (offen === 1) rest = i18n.t('One person has not finished yet.')
	else if (typeof offen === 'number') rest = i18n.t('{{val1}} people have not finished yet.', { val1: offen })

	return { stand, rest }
}

/**
 * Die bevorzugten Kontaktwege der Expert:in als Satzteil („E-Mail oder
 * Telefon") — dieselben Bezeichnungen wie im Profil.
 */
const kontaktwege = modes => {
	const list = (Array.isArray(modes) ? modes : []).map(mode => i18n.t(`contactMode${mode}`))
	if (!list.length) return null
	if (list.length === 1) return list[0]
	return i18n.t('listOr', { first: list.slice(0, -1).join(', '), last: list[list.length - 1] })
}

/** E-Mail und Telefon der Expert:in als anklickbare Zeile. */
const kontaktZeile = info => {
	if (!info?.supporterEmail && !info?.supporterPhone) return null
	return (
		<span className={styles.contactLine}>
			{info.supporterEmail && (
				<a className={styles.link} href={`mailto:${info.supporterEmail}`}>
					{info.supporterEmail}
				</a>
			)}
			{info.supporterEmail && info.supporterPhone && ' · '}
			{info.supporterPhone && (
				<a className={styles.link} href={`tel:${info.supporterPhone}`}>
					{info.supporterPhone}
				</a>
			)}
		</span>
	)
}

const tag = value => (value ? formatVienna(value, 'dd.MM.yyyy') : '')

/**
 * Was nach der Bewertung moeglich wird — je nachdem, wo die Sperre greift.
 *
 * `gate` kommt aus den Einstellungen (`ACTIVATION` oder `FIRST_REPORT`). Fehlt
 * er, bleibt es beim allgemeinen Satz: Etwas zu behaupten, das fuer diese
 * Unternehmensform gar nicht gilt, waere schlechter als die Auslassung.
 */
const naechsterSchrittSatz = gate => {
	if (gate === 'ACTIVATION') {
		return i18n.t('Next, your teacher assesses the concept — only then can the company be activated.')
	}

	if (gate === 'FIRST_REPORT') {
		return i18n.t('Next, your teacher assesses the concept — only then can you submit the financial report.')
	}

	/*
	 * `OFF` heisst: Fuer diese Unternehmensform wird das Konzept gar nicht
	 * verlangt. Die Lernstrecke steht trotzdem jeder offen — eine JBC kann sie
	 * also abschliessen. „Als Naechstes bewertet eure Lehrkraft das Konzept"
	 * waere dort schlicht falsch: Das tut niemand.
	 */
	if (gate === 'OFF') {
		return i18n.t(
			'The concept is currently not required for your Junior Company — you can still fetch your certificate.'
		)
	}

	return i18n.t('Next, your teacher assesses the concept.')
}

function getLayoutNotifications(
	type,
	info,
	reportsDeadline,
	companyType,
	invoicesOverdue,
	createdAt,
	isActivated,
	isNeedReview,
	updateCompanyInfo,
	activationsStatus,
	options
) {
	switch (type) {
		case TNotifications.COMPANY_CONFIRMATION: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Company verified')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Congratulations! Your company has been successfully verified by JA Austria. For the legitimation of your business, we have issued a company register extract'
							)}{' '}
							<span
								className={styles.link}
								href="/"
								onClick={e => {
									e.preventDefault()
									downoload(info?.companyCertificate, 'Company certificate.pdf')
								}}
							>
								[Link]
							</span>{' '}
							{i18n.t('for you. For the sale of your company shares, the signed warrants')}{' '}
							<Link to={TRoutes.SHARES} className={styles.link}>
								{i18n.t('link to submission')}
							</Link>{' '}
							{i18n.t('must be uploaded up to a max. amount of')} {info?.maxBudget}€{' '}
							{i18n.t('in the Share section.')}{' '}
							{i18n.t('Do not forget to prepare your financial report by')}{' '}
							{(companyType === 'JC' && (
								<>
									<span className={styles.deadlineText}>
										{i18n.t('Semiannual deadline')}:{' '}
										{reportsDeadline?.JC_semiannual &&
											formatVienna(reportsDeadline?.JC_semiannual, 'dd.MM.yyyy')}
									</span>
									{', '}
									<span className={styles.deadlineText}>
										{i18n.t('Annual deadline')}:{' '}
										{reportsDeadline?.JC_annual &&
											formatVienna(reportsDeadline?.JC_annual, 'dd.MM.yyyy')}
									</span>
								</>
							)) ||
								(companyType === 'JBC' && (
									<span className={styles.deadlineText}>
										{i18n.t('Deadline')}:{' '}
										{reportsDeadline?.JBC && formatVienna(reportsDeadline?.JBC, 'dd.MM.yyyy')}
									</span>
								)) ||
								(companyType === 'JCC' && (
									<span className={styles.deadlineText}>
										{i18n.t('Deadline')}:{' '}
										{reportsDeadline?.JCC && formatVienna(reportsDeadline?.JCC, 'dd.MM.yyyy')}
									</span>
								))}
							.
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.PARENTAL_CONFIRMATION: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Consent confirmed')}</h3>
						<span className={styles.description}>
							{i18n.t('The parental/guardian consent form of has been verified and confirmed.', {
								val1: info?.name
							})}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.TEACHER_CONFIRMATION: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Teacher Confirmation')}</h3>
						<span className={styles.description}>
							{i18n.t('Congratulation, your account was confirmed by an admin')}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.REJECT_COMPANY: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.titleRejectCompany}>{i18n.t('Company not approved')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Your company has been reviewed by JA Austria and unfortunately could not be approved for business yet.'
							)}{' '}
							<br />
							{info?.description && `${i18n.t('Explanation')}: ${info.description}`}
						</span>
					</div>
				</div>
			)
		}

		// Nachricht der Verwaltung aus dem Verlauf des Firmenprofils. Der Verweis
		// fuehrt in den Verlauf, damit die Nachricht im Zusammenhang lesbar bleibt
		// und nicht als Einzelmeldung ohne Vorgeschichte dasteht.
		//
		// Die Junior Company steht in der Ueberschrift: Eine Lehrkraft betreut
		// mehrere, und ohne den Namen ist an der Karte nicht zu erkennen, worum
		// es geht. Mitteilungen ohne Namen gibt es nur, wenn das Unternehmen
		// inzwischen geloescht wurde.
		case TNotifications.COMPANY_LOG_MESSAGE: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<BellIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{info?.companyName
								? i18n.t('Message from the administration about {{val1}}', {
										val1: info.companyName
								  })
								: i18n.t('Message from the administration')}
						</h3>
						<span className={styles.description}>
							{info?.message}
							<br />
							{info?.companyId && (
								<Link to={TRoutes.COMPANY_MESSAGES(info.companyId)} className={styles.link}>
									{i18n.t('Open message history')}
								</Link>
							)}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.REJECT_PARENTAL: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.titleRejectCompany}>{i18n.t('Consent rejected')}</h3>
						<span className={styles.description}>
							{i18n.t('The declaration of consent of the parents or legal guardians of was rejected.', {
								val1: info?.name
							})}
							<br />
							<br />
							{info?.description && `${i18n.t('Explanation')}: ${info?.description}`}
						</span>
					</div>
				</div>
			)
		}

		// case TNotifications.USERS_JOINING: {
		// 	return (
		// 		<div className={styles.message}>
		// 			<div>
		// 				{' '}
		// 				<InfoIcon className={styles.icon} />{' '}
		// 			</div>
		// 			<div>
		// 				<h3 className={styles.title}>{i18n.t('New User')}</h3>
		// 				<span className={styles.description}>
		// 					{info?.name} {`(${info?.role})`} {i18n.t('joined successfully to company !')}
		// 				</span>
		// 			</div>
		// 		</div>
		// 	)
		// }

		// case TNotifications.PAYMENT_REQUEST: {
		// 	return (
		// 		<div className={styles.message}>
		// 			<div>
		// 				{' '}
		// 				<InfoIcon className={styles.icon} />{' '}
		// 			</div>
		// 			<div>
		// 				<h3 className={styles.title}>{i18n.t('Payment')}</h3>
		// 				<span className={styles.description}>{i18n.t('You company has to make a payment invoice')}</span>
		// 			</div>
		// 		</div>
		// 	)
		// }

		case TNotifications.PAYMENT_DONE: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Invoice paid')}</h3>
						<span className={styles.description}>
							{i18n.t('The open invoice from for has been paid.', {
								val1: formatVienna(createdAt, 'dd.MM.yyyy'),
								val2: info?.amount
							})}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.PROGRAM_FINISH: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<AwardIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.titleFinish}>{i18n.t('Congratulation!')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Great Job, you have been awarded for participation in Junior Company Program - download your certificate'
							)}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.NEED_REVIEW: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Company under verfication')}</h3>
						<span className={styles.description}>
							{info?.name}{' '}
							{i18n.t('has submitted the company for verification and activation by JA Austria.')}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.ASSIGN_REQUEST: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Request from a teacher')}</h3>
						<span className={styles.description}>
							{i18n.t('A teacher from')} {info.schoolName}{' '}
							{i18n.t('is interested in your company. Please add')} {info.salutation} {info.name}{' '}
							{i18n.t('to your company in the company profile section.')}
							<br />
							<Link
								to="#"
								className={styles.link}
								onClick={() => eventEmitter.emit(types.openAddTeacherModal)}
							>
								{i18n.t('Open a modal window and confirm or reject the request.')}
							</Link>{' '}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.REJECT_ASSIGN_REQUEST: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.titleRejectCompany}>{i18n.t('Assign Request')}</h3>
						<span className={styles.description}>
							{i18n.t('Your request was rejected. Company')} - {info?.company}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.INVOICE_PAYMENT_REMINDER: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<BellIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Invoice payment reminder')}</h3>
						<span className={styles.description}>
							{i18n.t('The report has been submitted, the invoice was created. Please pay the invoice.')}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.REJECT_REPORT: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Financial report rejected')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Attention. Your financial report has been put on hold after submission and needs to be resubmitted after revision.'
							)}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.REPORT_REMINDER: {
			/*
			 * `info.days` ist die vorzeichenbehaftete Differenz zur Frist, die der
			 * Erinnerungslauf mitgibt: negativ = noch so viele Tage Zeit, positiv =
			 * so viele Tage ueberfaellig. Seit der Vorlauf greift (Anforderung #90,
			 * Punkt 2) kommt die erste Erinnerung VOR der Frist — dort darf nicht
			 * "ueberfaellig" stehen. Fehlt die Angabe, bleibt es beim neutralen Satz.
			 */
			const daysToDeadline = Number(info?.days)
			const reminderText = !Number.isFinite(daysToDeadline)
				? i18n.t('Please do not forget to submit your financial report.')
				: (daysToDeadline === 1 && i18n.t('The financial report is overdue by one day.')) ||
				  (daysToDeadline > 1 &&
						i18n.t('The financial report is overdue by several days.', { days: daysToDeadline })) ||
				  (daysToDeadline === -1 && i18n.t('The financial report is due tomorrow.')) ||
				  (daysToDeadline < -1 &&
						i18n.t('The financial report is due in several days.', { days: -daysToDeadline })) ||
				  i18n.t('The financial report is due today.')

			return (
				<div className={styles.message}>
					<div>
						{' '}
						<BellIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Financial report reminder')}</h3>
						<span className={styles.description}>
							{reminderText}{' '}
							{(companyType === 'JC' && (
								<>
									<span className={styles.deadlineText}>
										{i18n.t('Half-year report')}:{' '}
										{reportsDeadline?.JC_semiannual &&
											formatVienna(reportsDeadline?.JC_semiannual, 'dd.MM.yyyy')}
									</span>
									{', '}
									<span className={styles.deadlineText}>
										{i18n.t('annual report')}:{' '}
										{reportsDeadline?.JC_annual &&
											formatVienna(reportsDeadline?.JC_annual, 'dd.MM.yyyy')}
									</span>
								</>
							)) ||
								(companyType === 'JBC' && (
									<span className={styles.deadlineText}>
										{i18n.t('Deadline')}:{' '}
										{reportsDeadline?.JBC && formatVienna(reportsDeadline?.JBC, 'dd.MM.yyyy')}
									</span>
								)) ||
								(companyType === 'JCC' && (
									<span className={styles.deadlineText}>
										{i18n.t('Deadline')}:{' '}
										{reportsDeadline?.JCC && formatVienna(reportsDeadline?.JCC, 'dd.MM.yyyy')}
									</span>
								))}
							.
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.INVITE_EMPLOYEE: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('New employee invited')}</h3>
						<span className={styles.description}>
							{i18n.t('has invited to work in the company.', {
								val1: info?.userName,
								val2: info?.name
							})}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.INVITE_TEACHER: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Teacher invited')}</h3>
						<span className={styles.description}>
							{i18n.t('has invited the teacher to the enterprise.', {
								val1: info?.userName,
								val2: info?.name
							})}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.ADD_EXPERT: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Economic expert added')}</h3>
						<span className={styles.description}>
							{i18n.t('has registered as a company expert.', {
								val1: info?.userName,
								val2: info?.name
							})}
						</span>
					</div>
				</div>
			)
		}

		/*
		 * Ein selbst erfasster Alteintrag ist nach seiner Frist geloescht
		 * worden. Er zaehlte fuer die Voraussetzung "Mindestens 1
		 * Wirtschaftsexpert:in" — faellt er weg, ohne dass es jemand sagt,
		 * steht am naechsten Tag ein Haken weniger in der Checkliste.
		 */
		case TNotifications.EXPERT_RECORD_REMOVED: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('expertRecordRemovedTitle')}</h3>
						<span className={styles.description}>
							{i18n.t('expertRecordRemovedText', { name: info?.name, days: info?.days })}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.CONFIRM_EXPERT: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Economic expert confirmed')}</h3>
						<span className={styles.description}>
							{i18n.t('Business expert agrees with the GDPR.', {
								val1: info?.name
							})}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.SUBMIT_RISK: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Market- & Riskanalysis')}</h3>
						<span className={styles.description}>
							{i18n.t('Great, you have dealt with the market and risk analysis.')}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.SUBMIT_BUSINESS_PLAN: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{'Businessplan'}</h3>
						<span className={styles.description}>
							{i18n.t('Your business plan has been successfully uploaded.')}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.SUBMIT_PARENTAL: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Parent/guardian consent')}</h3>
						<span className={styles.description}>
							{info?.name} {i18n.t('has uploaded the parent/guardian consent form.')}
						</span>
					</div>
				</div>
			)
		}

		/*
		 * Die drei Mitteilungen zum Kursnachweis entstehen seit dem Ausbau
		 * (31.10.2026) nicht mehr. Ältere stehen noch in der Datenbank und
		 * werden weiter angezeigt — mit dem festen Namen, weil die Einstellung
		 * mit der eigenen Bezeichnung entfallen ist.
		 */
		case TNotifications.SUBMIT_COURSE_CERTIFICATE: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Course Certificate')}</h3>
						<span className={styles.description}>
							{info?.name} {i18n.t('has uploaded the course certificate.')}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.COURSE_CERTIFICATE_CONFIRMATION: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Course certificate confirmed')}</h3>
						<span className={styles.description}>
							{i18n.t('The course certificate of has been verified and confirmed.', {
								val1: info?.name
							})}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.REJECT_COURSE_CERTIFICATE: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.titleRejectCompany}>{i18n.t('Course certificate rejected')}</h3>
						<span className={styles.description}>
							{i18n.t('The course certificate of was rejected.', {
								val1: info?.name
							})}
							<br />
							<br />
							{info?.description && `${i18n.t('Explanation')}: ${info?.description}`}
						</span>
					</div>
				</div>
			)
		}

		/*
		 * Ein Nachweis wurde von der Lehrkraft entfernt (Anforderung #94).
		 * Bewusst eine eigene Nachricht und nicht REJECT_PARENTAL: Dort liegt die
		 * Datei noch und laesst sich ansehen — hier ist sie weg, und genau das
		 * muss die Schueler:in erfahren, sonst wartet sie auf eine Pruefung, die
		 * nie kommt.
		 */
		case TNotifications.REQUIREMENT_DOCUMENT_REMOVED: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.titleRejectCompany}>{i18n.t('Document removed')}</h3>
						<span className={styles.description}>
							{i18n.t('{{val1}} was removed by your teacher. Please upload the correct file again.', {
								val1: info?.document || i18n.t('The document')
							})}
							<br />
							<br />
							{info?.reason && `${i18n.t('Explanation')}: ${info?.reason}`}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.FINISH_COMPANY: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<AwardIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Participation certificate')}</h3>
						<span className={styles.description}>
							{i18n.t('Congratulations')} {info?.name}!{' '}
							{i18n.t('You can find your certificate in the "Documents" section')}{' '}
							<span
								className={styles.link}
								href="/"
								onClick={e => {
									e.preventDefault()
									/*
									 * Diese Mitteilung bekommen nur Schüler:innen, und der
									 * Download läuft über ihre Schnittstelle. Nach dem
									 * Anstoßen kommt für JC/JCC der Hinweis auf die
									 * Unterstützer:innen-Registrierung (#212).
									 */
									downoload(info?.participationCertificate, 'Perticipation certificate.pdf', () =>
										maybeShowSupporterInvitation({
											role: TRoles.STUDENT,
											companyType,
											documentType: DOCUMENT_TYPES.PARTICIPATION_DOCUMENT
										})
									)
								}}
							>
								{i18n.t('link to certificate of participation')}
							</span>{' '}
							.
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.FIRST_ENTER: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Welcome !')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Here you will find information about upcoming tasks around your company as well as hints about interesting offers and events of our partners.'
							)}
							<br />
							<br />
							{i18n.t(
								'First enter all relevant information about your company, add further employees, supervising teachers and company experts. Optionally, you can also add a business plan. Do not forget to upload the signed consent of a parent or guardian in your user profile.'
							)}
							<br />
							<br />
							{i18n.t(
								'Before your company starts business, it must be submitted to JA Austria for verification and activation. After activation you can offer your products, services and shares in your company on the market - for the legitimisation of these activities you will receive a company register extract and the template for issuing warrants.'
							)}
							<br />
							<br />
							{i18n.t(
								'The financial report provides you with information on the necessary taxes that are usually incurred in the course of business activities.'
							)}
							<br />
							<br />
							{i18n.t('Good luck for your company')}
							<br />
							{i18n.t('JA Austria Team')}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.ACTIVATE_COMPANY_REMINDER: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<BellIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Reminder')}</h3>
						<span className={styles.description}>
							{i18n.t("Don't forget to submit your company for approval (button in dashboard area)")}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.SUBMIT_APPROVAL_COMPANY_REMINDER: {
			return (
				<div className={styles.message}>
					<div>
						{' '}
						<BellIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Company Activation')}</h3>
						<span className={styles.description}>
							{info?.companyName} {i18n.t('is waiting for an activation check for')}{' '}
							{info?.firstReminderSubmittedApproval} {i18n.t('days!')}{' '}
							<Link to={TRoutes.COMPANY_REVIEW(info.companyId)} className={styles.link}>
								{i18n.t('Company activation page.')}
							</Link>{' '}
						</span>
					</div>
				</div>
			)
		}

		// case TNotifications.SUBMIT_FINAL_REPORT: {
		// 	return (
		// 		<div className={styles.message}>
		// 			<div>
		// 				{' '}
		// 				<SuccessIcon className={styles.icon} />{' '}
		// 			</div>
		// 			<div>
		// 				<h3 className={styles.title}>{i18n.t('Business Report')}</h3>
		// 				<span className={styles.description}>{i18n.t('Your business report has been successfully uploaded.')}</span>
		// 			</div>
		// 		</div>
		// 	)
		// }
		case TNotifications.INVOICE_GENERATED: {
			/*
			 * Fällig ist die Rechnung an einem festen Tag: Ausstellung plus die
			 * Frist aus den Einstellungen. Hier stand vorher die Restzeit, bei
			 * jedem Ansehen neu gerechnet — dieselbe Mitteilung las sich nach
			 * Ablauf der Frist als „innerhalb der nächsten -1 Tage".
			 */
			const overdueDays = Number(invoicesOverdue?.value)
			const dueDate =
				Number.isFinite(overdueDays) && overdueDays > 0
					? formatVienna(add(new Date(createdAt), { days: overdueDays }), 'dd.MM.yyyy')
					: null

			return (
				<div className={styles.message}>
					<div>
						<BellIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Invoice generated')}</h3>
						<span className={styles.description}>
							{i18n.t('We have created an')}{' '}
							<Link
								to="#"
								className={styles.deadlineText}
								onClick={e => {
									e.preventDefault()
									downoload(info?.name, 'invoice.pdf')
								}}
							>
								{i18n.t('invoice')}
							</Link>{' '}
							{dueDate
								? `${i18n.t('based on your report. Please pay it by')} ${dueDate}.`
								: i18n.t('based on your report.')}{' '}
							{i18n.t(`You can find the invoice in the`)}{' '}
							<Link to={TRoutes.DOCUMENTS} className={styles.deadlineText}>
								"{i18n.t('documents')}"
							</Link>
							{' .'}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.INVOICE_PART_PAID: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Invoice amount paid incompletely')}</h3>
						<span className={styles.description}>
							{i18n.t(
								`The open invoice from for was paid incompletely. Your company account has a balance of. Please transfer the difference to the account details provided.`,
								{
									val1: formatVienna(info?.createdAt, 'dd.MM.yyyy'),
									val2: info?.amount,
									val3: info?.balance
								}
							)}
						</span>
					</div>
				</div>
			)
		}

		case TNotifications.TEACHER_FIRST_ENTER: {
			return (
				<div className={styles.message}>
					<div>
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Welcome !')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Here you will find information about activities of the companies you support as well as information about interesting offers and events of our partners.'
							)}
							<br />
							<br />
							{i18n.t('Good luck for your company')}
							<br />
							{i18n.t('JA Austria Team')}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.TEACHER_NEW_COMPANY: {
			return (
				<div className={styles.message}>
					<div>
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('New company')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'The JA Austria company has added you as a supervising teacher. You can now follow the details of the assigned companies in the "Companies" section and also have access to their documents such as business plan, shareholder certificates, financial reports and annual report.',
								{ val1: info?.companyName }
							)}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.TEACHER_COMPANY_ACTIVATED: {
			return (
				<div className={styles.message}>
					<div>
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Company activated')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Congratulations! The company you are in charge of has been checked by JA Austria and activated for further business activities. The students have received an extract from the company register and can now sell shares in the company up to a total value of EUR . The following date(s) have been set for report submission',
								{ val1: info?.companyName, val2: info?.shares }
							)}{' '}
							{(info?.companyType === 'JC' && (
								<>
									<span className={styles.deadlineText}>
										{i18n.t('Annual deadline')}:{' '}
										{reportsDeadline?.JC_semiannual &&
											formatVienna(reportsDeadline?.JC_annual, 'dd.MM.yyyy')}
									</span>{' '}
									<span className={styles.deadlineText}>
										{i18n.t('Semiannual deadline')}:{' '}
										{reportsDeadline?.JC_semiannual &&
											formatVienna(reportsDeadline?.JC_semiannual, 'dd.MM.yyyy')}
									</span>
								</>
							)) ||
								(info?.companyType === 'JBC' && (
									<span className={styles.deadlineText}>
										{i18n.t('Deadline')}:{' '}
										{reportsDeadline?.JBC && formatVienna(reportsDeadline?.JBC, 'dd.MM.yyyy')}
									</span>
								)) ||
								(info?.companyType === 'JCC' && (
									<span className={styles.deadlineText}>
										{i18n.t('Deadline')}:{' '}
										{reportsDeadline?.JC_semiannual &&
											formatVienna(reportsDeadline?.JCC, 'dd.MM.yyyy')}
									</span>
								))}
							.
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.TEACHER_REPORT_SUBMITTED: {
			return (
				<div className={styles.message}>
					<div>
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Report submitted')}</h3>
						<span className={styles.description}>
							{i18n.t('The company you supervise has filed. You can find the report here', {
								val1: info?.companyName,
								val2: i18n.t(`${info?.reportType}`)
							})}
							<Link to={TRoutes.REPORTS_TABLE} className={styles.link}>
								{i18n.t('to the reports')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.TEACHER_FINANCIAL_REPORT_SUBMITTED: {
			return (
				<div className={styles.message}>
					<div>
						<AwardIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('Financial report submitted & certificates have been issued')}
						</h3>
						<span className={styles.description}>
							{i18n.t(
								'Congratulations! Your company has submitted its financial report and thus successfully completed the JA Austria Programme. For the students involved in the company we have deposited completion certificates in the "Documents" section - the students can download them independently. You can find the business report here ',
								{
									// `compamyName` war ein Tippfehler — der Platzhalter blieb leer,
									// die Mitteilung nannte also nicht, welche Junior Company gemeint
									// ist. Mitteilungen von vor dem 08.08.2026 tragen den Namen gar
									// nicht; dort bleibt die Stelle leer.
									val1: info?.companyName || info?.compamyName || ''
								}
							)}
							<span
								className={styles.link}
								onClick={e => {
									e.preventDefault()
									downoloadTeacher(info?.finalReportName, 'final report.pdf')
								}}
							>
								{' '}
								{i18n.t('to the business report')}
							</span>{' '}
							.
							<br />
							<br />
							{i18n.t(
								'We would also like to thank you very much for your support of the JA Austria programme with a certificate'
							)}
							<span
								className={styles.link}
								onClick={e => {
									e.preventDefault()
									downoloadTeacher(info?.participationCertificate, 'participation certificate.pdf')
								}}
							>
								{' '}
								{i18n.t('to the certificate')}
							</span>{' '}
							<br />
							{i18n.t('JA Austria Team')}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.TEACHER_REPORT_REMINDER: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Overdue reports')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Attention the following JA Austria companies you are in charge of have not yet submitted their report in our system and therefore cannot successfully complete the programme. Company: '
							)}
							<ul>
								{info?.companies?.map(v => (
									<li>{v.name}</li>
								))}
							</ul>

							<Link to={TRoutes.REPORTS_TABLE} className={styles.link}>
								{i18n.t('to the reports')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.TEACHER_INVOICE_REMINDER: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Overdue reports')}</h3>
						<span className={styles.description}>
							{i18n.t('The company you supervise has filed. You can find the report here', {
								val1: info?.companyName || info?.compamyName || '',
								val2: info?.reportType
							})}
							<Link to={TRoutes.REPORTS_TABLE} className={styles.link}>
								{i18n.t('to the reports')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.COMPANY_READY_FOR_SUBMISSION: {
			return (
				<div className={styles.message}>
					<div>
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Ready for submission')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Congratulations you have everything complete. Your company can now be submitted to JA Austria for review.'
							)}{' '}
							{!isNeedReview && !isActivated && (
								<Btn
									onClick={() =>
										// Dasselbe Fenster wie hinter „Zur Genehmigung
										// einreichen" auf dem Dashboard, siehe
										// shared/approvalDialog.
										openApprovalRequirements({
											options,
											activationsStatus,
											onSubmitted: updateCompanyInfo
										})
									}
									className={styles.btnSubmitReview}
									theme="minimal"
								>
									{i18n.t('Submit for approval')}
								</Btn>
							)}
						</span>
					</div>
				</div>
			)
		}
		/*
		 * Der Eingang des Berichts, aus Sicht der Schüler:innen. Bisher bestätigte
		 * ihn nichts: Sichtbar wurde erst die Rechnung, die daraus entstand. Nach
		 * einer Zurückweisung las sich die Liste deshalb so, als sei nie ein neuer
		 * Bericht abgegeben worden.
		 */
		case TNotifications.REPORT_SUBMITTING: {
			return (
				<div className={styles.message}>
					<div>
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Report submitted')}</h3>
						<span className={styles.description}>
							{i18n.t('Your report has been submitted successfully', {
								val1: i18n.t(`${info?.reportType}`)
							})}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.REPORT_SUBMITTING_FINAL_REPORT: {
			return (
				<div className={styles.message}>
					<div>
						<BellIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Final Report')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Please upload your final report via the upload function in the dashboard. Afterwards you will find your participation certificates for the successful completion of the JA Austria programme in the document section.'
							)}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.CREATING_NEWS:
		case TNotifications.CREATING_EVENT: {
			const isNews = info.newsType === TNews.NEWS
			return (
				<div className={styles.message}>
					<div>{isNews ? <NewsIcon className={styles.icon} /> : <EventIcon className={styles.icon} />} </div>
					<div>
						<h3 className={styles.title}>{isNews ? i18n.t('news') : i18n.t('event')}</h3>
						<span className={styles.description}>
							<Link
								to="#"
								className={styles.link}
								onClick={() =>
									eventEmitter.emit(types.openDetailFeedModal, {
										id: info.id,
										title: info.title,
										description: info.description,
										externalLink: info.externalLink,
										place: info.place,
										startDate: info.startDate,
										endDate: info.endDate,
										states: info.states,
										roles: info.roles,
										companyTypes: info.companyTypes
									})
								}
							>
								{` ${info.title}`}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		/*
		 * Lebensmittelhygiene und Allergeninformation (Anforderung #96).
		 *
		 * Drei Karten: die Bestätigung an die Lehrkraft, ihr Widerspruch zurück
		 * an die Schüler:innen, die Erinnerung an die noch offene Schulung an
		 * beide. Alle drei führen an die Stelle, an der zu handeln ist —
		 * Lehrkraft aufs eigene Dashboard, Schüler:innen ins Unternehmensprofil.
		 */
		case TNotifications.HYGIENE_CONFIRMED: {
			return (
				<div className={styles.message}>
					<div>
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Food hygiene: confirmation submitted')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'The Junior Company {{val1}} operates in a sensitive branch ({{val2}}) and has confirmed: {{val3}}',
								{
									val1: info?.companyName,
									// Die Branche steht englisch in der Mitteilung — so ist sie
									// gespeichert (siehe shared/optionList).
									val2: optionLabel(options?.branch, info?.branch, i18n.language),
									val3: info?.statusLabel
								}
							)}{' '}
							{!!info?.participants?.length && (
								<>
									{i18n.t('Participants')}: {info.participants.join(', ')}.{' '}
								</>
							)}
							<Link to={TRoutes.DASHBOARD} className={styles.link}>
								{i18n.t('to the dashboard')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		/*
		 * Skills4Retail-Konzept. Die erste Meldung geht an die betreuende
		 * Lehrkraft, die drei folgenden an die Schueler:innen — der Verweis
		 * fuehrt jeweils auf dieselbe Seite, dort sieht jede Rolle ihre eigene
		 * Ansicht.
		 */
		case TNotifications.CONCEPT_SUBMITTED: {
			return (
				<div className={styles.message}>
					<div>
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Concept awaiting your assessment')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'The Junior Company {{val1}} has completed its concept on e-commerce and digital marketing. Please assess the fifteen fields with 0 to 10 points each.',
								{ val1: info?.companyName }
							)}{' '}
							<Link to={TRoutes.CONCEPT} className={styles.link}>
								{i18n.t('to the concept')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.CONCEPT_RETURNED: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Concept returned for revision')}</h3>
						<span className={styles.description}>
							{i18n.t('{{val1}} has returned your concept for revision. You can edit it again.', {
								val1: info?.teacherName
							})}{' '}
							{info?.reason && <>„{info.reason}" </>}
							<Link to={TRoutes.CONCEPT} className={styles.link}>
								{i18n.t('to the concept')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		/*
		 * Wirtschaftsexpert:in hat zugesagt (Anforderung #232). Die
		 * Schüler:innen sind jetzt am Zug — die Karte sagt bis wann und wie,
		 * mit den Kontaktdaten gleich darin.
		 */
		case TNotifications.SUPPORTER_REQUEST_ACCEPTED: {
			const wege = kontaktwege(info?.contactModes)
			return (
				<div className={styles.message}>
					<div>
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('supporterAcceptedTitle', { name: info?.supporterName || '' })}
						</h3>
						<span className={styles.description}>
							{i18n.t('supporterAcceptedText', {
								name: info?.supporterName || '',
								company: info?.companyName || '',
								days: info?.contactDays,
								until: tag(info?.contactUntil)
							})}{' '}
							{wege && i18n.t('supporterAcceptedModes', { name: info?.supporterName || '', modes: wege })}{' '}
							{i18n.t('supporterAcceptedTip')}{' '}
							<Link to={TRoutes.SUPPORTERS} className={styles.link}>
								{i18n.t('toExperts')}
							</Link>
							{kontaktZeile(info)}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.SUPPORTER_REQUEST_ACCEPTED_TEACHER: {
			return (
				<div className={styles.message}>
					<div>
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('supporterAcceptedTeacherTitle')}</h3>
						<span className={styles.description}>
							{i18n.t('supporterAcceptedTeacherText', {
								name: info?.supporterName || '',
								company: info?.companyName || '',
								days: info?.contactDays,
								until: tag(info?.contactUntil)
							})}
						</span>
					</div>
				</div>
			)
		}
		/*
		 * Die Rückfrage nach Ablauf der Frist — mit Ja/Nein direkt in der Karte
		 * (ContactCheck). Ob schon geantwortet wurde, steht in `info.answer`.
		 */
		case TNotifications.SUPPORTER_CONTACT_CHECK: {
			return (
				<div className={styles.message}>
					<div>
						<BellIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('contactCheckTitle', { name: info?.supporterName || '' })}
						</h3>
						<span className={styles.description}>
							{info?.promisedUntil
								? i18n.t('contactCheckTextPromised', {
										name: info?.supporterName || '',
										date: tag(info.promisedUntil)
								  })
								: i18n.t('contactCheckText', {
										name: info?.supporterName || '',
										date: tag(info?.acceptedAt),
										company: info?.companyName || ''
								  })}
							{kontaktZeile(info)}
						</span>
						<ContactCheck info={info} />
					</div>
				</div>
			)
		}
		case TNotifications.CONCEPT_SCORED: {
			return (
				<div className={styles.message}>
					<div>
						<AwardIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Concept assessed')}</h3>
						<span className={styles.description}>
							{i18n.t('{{val1}} has assessed your concept: {{val2}} out of {{val3}} points.', {
								val1: info?.teacherName,
								val2: info?.total,
								val3: info?.max
							})}{' '}
							<Link to={TRoutes.CONCEPT} className={styles.link}>
								{i18n.t('to the concept')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		/*
		 * Jemand hat die Lernstrecke abgeschlossen.
		 *
		 * Zwei Zweige aus einem Anlass, mit denselben Zahlen: die Gratulation an
		 * die Person selbst, der Stand an die betreuende Lehrkraft. Der Balken
		 * darunter zeigt, wie weit das Konzept insgesamt ist — sonst waere die
		 * Meldung eine Zahl ohne Zusammenhang.
		 */
		case TNotifications.COURSE_COMPLETED: {
			const { stand, rest } = kursStandSaetze(info)

			return (
				<div className={styles.message}>
					<div>
						<AwardIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Course completed — congratulations!')}</h3>
						<span className={styles.description}>
							{i18n.t('You have passed every chapter of the Skills4Retail course.')} {stand} {rest}{' '}
							{naechsterSchrittSatz(info?.gate)}{' '}
							<Link to={TRoutes.CONCEPT} className={styles.link}>
								{i18n.t('to the concept')}
							</Link>
						</span>
						<ConceptProgress
							compact
							progress={
								info?.conceptPercent === null || info?.conceptPercent === undefined
									? null
									: { percent: info.conceptPercent, stages: info.conceptStages }
							}
						/>
					</div>
				</div>
			)
		}
		case TNotifications.COURSE_COMPLETED_TEACHER: {
			const { stand, rest } = kursStandSaetze(info)

			return (
				<div className={styles.message}>
					<div>
						<AwardIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('{{val1}} has completed the course', {
								val1: info?.studentName || i18n.t('A student')
							})}
						</h3>
						<span className={styles.description}>
							{i18n.t('In the Junior Company {{val1}}:', { val1: info?.companyName })} {stand} {rest}{' '}
							<Link to={TRoutes.CONCEPT} className={styles.link}>
								{i18n.t('to the concept')}
							</Link>
						</span>
						<ConceptProgress
							compact
							progress={
								info?.conceptPercent === null || info?.conceptPercent === undefined
									? null
									: { percent: info.conceptPercent, stages: info.conceptStages }
							}
						/>
					</div>
				</div>
			)
		}
		/*
		 * Kurse mit Urkunde (Kursverwaltung, KI-Kurs) — nicht die Lernstrecke
		 * Skills4Retail darüber, die ihre eigenen Arten hat.
		 */
		case TNotifications.LEARNING_COURSE_COMPLETED: {
			return (
				<div className={styles.message}>
					<div>
						<AwardIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('Course „{{title}}“ completed — congratulations!', { title: info?.courseTitle || '' })}
						</h3>
						<span className={styles.description}>
							{i18n.t('Your certificate is ready.')}{' '}
							{info?.courseId && (
								<Link to={TRoutes.COURSE(info.courseId)} className={styles.link}>
									{i18n.t('To the certificate')}
								</Link>
							)}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.LEARNING_COURSE_COMPLETED_TEACHER: {
			const namen = Array.isArray(info?.names) ? info.names.filter(Boolean) : []
			const anzahl = info?.count || namen.length || 1

			return (
				<div className={styles.message}>
					<div>
						<AwardIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{anzahl === 1
								? i18n.t('One student has completed „{{title}}“', { title: info?.courseTitle || '' })
								: i18n.t('{{n}} students have completed „{{title}}“', {
										n: anzahl,
										title: info?.courseTitle || ''
								  })}
						</h3>
						<span className={styles.description}>
							{info?.companyName && i18n.t('In the Junior Company {{val1}}:', { val1: info.companyName })}{' '}
							{namen.join(', ')}{' '}
							<Link to={TRoutes.COURSES} className={styles.link}>
								{i18n.t('To the course overview')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.LEARNING_COURSE_CREDIT_CAP_REACHED: {
			return (
				<div className={styles.message}>
					<div>
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Maximum course credit reached')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'Your Junior Company has earned the full credit of {{amount}} for „{{title}}“. It is deducted from the last invoice of the school year.',
								{
									amount: info?.amount !== undefined && info?.amount !== null ? `${info.amount} €` : '',
									title: info?.courseTitle || ''
								}
							)}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.CONCEPT_ASSESSMENT_REMINDER: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Concept assessment still pending')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'The concept of the Junior Company {{val1}} has been waiting {{val2}} days for your assessment.',
								{ val1: info?.companyName, val2: info?.days }
							)}{' '}
							<Link to={TRoutes.CONCEPT} className={styles.link}>
								{i18n.t('to the concept')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.HYGIENE_OBJECTION: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Food hygiene: objection by your teacher')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'{{val1}} has objected to your confirmation on food hygiene and allergen information. Please clarify it together and confirm again.',
								{ val1: info?.teacherName }
							)}{' '}
							{info?.reason && <>„{info.reason}" </>}
							<Link to={TRoutes.COMPANY_TAB('business')} className={styles.link}>
								{i18n.t('To the business activity')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.HYGIENE_TRAINING_REMINDER: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Food hygiene training still open')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'For {{val1}} the food hygiene and allergen training has not been completed yet. It must be completed before production begins.',
								{ val1: info?.companyName }
							)}{' '}
							<Link to={TRoutes.COMPANY_TAB('business')} className={styles.link}>
								{i18n.t('To the business activity')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.SCHOOL_YEAR_RESET_REMINDER: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>{i18n.t('Download your documents before the annual reset')}</h3>
						<span className={styles.description}>
							{i18n.t(
								'On {{val1}} all Junior Companies and student accounts will be deleted. Please download everything you want to keep beforehand — certificates, reports and documents.',
								{ val1: info?.dateLabel }
							)}{' '}
							<Link to={TRoutes.DOCUMENTS} className={styles.link}>
								{i18n.t('To the documents')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		/*
		 * Anfragen über den Hilfe-Knopf. Beide Karten tragen die Nummer: Sie ist
		 * das, was man später nennt — in einer Rückfrage, am Telefon, in einer
		 * E-Mail.
		 */
		case TNotifications.REQUEST_RECEIVED: {
			return (
				<div className={styles.message}>
					<div>
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('Your request has been received')} #{info?.ticketId}
						</h3>
						<span className={styles.description}>
							{info?.title}
							{' — '}
							{i18n.t('We will get back to you here.')}
						</span>
					</div>
				</div>
			)
		}
		/*
		 * Der Rueckweg: Was die meldende Person betrifft, fuehrt in „Meine
		 * Anfragen" — mit der Nummer in der Adresse, damit der Vorgang gleich
		 * aufgeht und niemand suchen muss.
		 */
		case TNotifications.REQUEST_ANSWERED: {
			return (
				<div className={styles.message}>
					<div>
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('Your request has been answered')} #{info?.ticketId}
						</h3>
						<span className={styles.description}>
							{info?.title}{' '}
							<Link to={`${TRoutes.MY_REQUESTS}?nr=${info?.ticketId}`} className={styles.link}>
								{i18n.t('Read the answer')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.REQUEST_QUESTION: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('Question about your request')} #{info?.ticketId}
						</h3>
						<span className={styles.description}>
							{info?.title}{' '}
							<Link to={`${TRoutes.MY_REQUESTS}?nr=${info?.ticketId}`} className={styles.link}>
								{i18n.t('Answer now')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.REQUEST_CLOSED: {
			return (
				<div className={styles.message}>
					<div>
						<SuccessIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('Your request is closed')} #{info?.ticketId}
						</h3>
						<span className={styles.description}>{info?.title}</span>
					</div>
				</div>
			)
		}
		case TNotifications.REQUEST_REJECTED: {
			return (
				<div className={styles.message}>
					<div>
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('Your request will not be pursued')} #{info?.ticketId}
						</h3>
						<span className={styles.description}>
							{info?.title}
							{info?.reason ? ` — ${info.reason}` : ''}
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.REQUEST_REPLY: {
			return (
				<div className={styles.message}>
					<div>
						<InfoIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('Reply to request')} #{info?.ticketId}
						</h3>
						<span className={styles.description}>
							{info?.title}
							{info?.reporter ? ` — ${info.reporter}` : ''}{' '}
							<Link to={TRoutes.TICKETS} className={styles.link}>
								{i18n.t('To the requirements')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.REQUEST_OVERDUE: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{info?.escalated ? i18n.t('Request still unanswered') : i18n.t('Request is waiting')} #
							{info?.ticketId}
						</h3>
						<span className={styles.description}>
							{info?.title}{' '}
							<Link to={TRoutes.TICKETS} className={styles.link}>
								{i18n.t('To the requirements')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		case TNotifications.REQUEST_NEW: {
			return (
				<div className={styles.message}>
					<div>
						<AlertIcon className={styles.icon} />{' '}
					</div>
					<div>
						<h3 className={styles.title}>
							{i18n.t('New request')} #{info?.ticketId}
						</h3>
						<span className={styles.description}>
							{info?.title}
							{info?.reporter ? ` — ${info.reporter}` : ''}{' '}
							<Link to={TRoutes.TICKETS} className={styles.link}>
								{i18n.t('To the requirements')}
							</Link>
						</span>
					</div>
				</div>
			)
		}
		default:
			return null
	}
}

/*
 * Der Anlass einer Mitteilung bestimmt ihre Farbe: Zusage grün, Absage rot,
 * Erinnerung orange, Neuigkeit türkis-hell, alles Übrige das Türkis der Marke.
 * Die Farbe sitzt am Kartenrand und hinter dem Sinnbild — so ist beim
 * Überfliegen einer langen Liste zu sehen, was eine Handlung verlangt.
 */
const TONES = {
	[TNotifications.COMPANY_CONFIRMATION]: 'success',
	[TNotifications.PARENTAL_CONFIRMATION]: 'success',
	[TNotifications.COURSE_CERTIFICATE_CONFIRMATION]: 'success',
	[TNotifications.PAYMENT_DONE]: 'success',
	[TNotifications.PROGRAM_FINISH]: 'success',
	[TNotifications.SUBMIT_RISK]: 'success',
	[TNotifications.SUBMIT_BUSINESS_PLAN]: 'success',
	[TNotifications.SUBMIT_PARENTAL]: 'success',
	[TNotifications.SUBMIT_COURSE_CERTIFICATE]: 'success',
	[TNotifications.FINISH_COMPANY]: 'success',
	[TNotifications.TEACHER_COMPANY_ACTIVATED]: 'success',
	[TNotifications.TEACHER_FINANCIAL_REPORT_SUBMITTED]: 'success',
	[TNotifications.COMPANY_READY_FOR_SUBMISSION]: 'success',
	[TNotifications.REPORT_SUBMITTING]: 'success',

	[TNotifications.REJECT_COMPANY]: 'danger',
	[TNotifications.REJECT_PARENTAL]: 'danger',
	[TNotifications.REJECT_REPORT]: 'danger',
	[TNotifications.REJECT_ASSIGN_REQUEST]: 'danger',
	[TNotifications.REJECT_COURSE_CERTIFICATE]: 'danger',
	[TNotifications.REQUIREMENT_DOCUMENT_REMOVED]: 'danger',
	[TNotifications.INVOICE_PART_PAID]: 'danger',

	[TNotifications.INVOICE_GENERATED]: 'warning',
	[TNotifications.INVOICE_PAYMENT_REMINDER]: 'warning',
	[TNotifications.REPORT_REMINDER]: 'warning',
	[TNotifications.REPORT_SUBMITTING_FINAL_REPORT]: 'warning',
	[TNotifications.ACTIVATE_COMPANY_REMINDER]: 'warning',
	[TNotifications.SUBMIT_APPROVAL_COMPANY_REMINDER]: 'warning',
	[TNotifications.TEACHER_REPORT_REMINDER]: 'warning',
	[TNotifications.TEACHER_INVOICE_REMINDER]: 'warning',
	[TNotifications.HYGIENE_TRAINING_REMINDER]: 'warning',
	// Kein „danger": Angekündigt wird ein geplanter Vorgang, kein Fehler — und
	// bis zum Datum lässt sich alles noch herunterladen.
	[TNotifications.SCHOOL_YEAR_RESET_REMINDER]: 'warning',

	// Der Widerspruch der Lehrkraft setzt die Bestätigung außer Kraft — das ist
	// eine Absage, keine Erinnerung.
	[TNotifications.HYGIENE_OBJECTION]: 'danger',

	[TNotifications.CREATING_NEWS]: 'news',
	[TNotifications.CREATING_EVENT]: 'news',

	// Der Eingang ist eine Zusage („angekommen"), die neue Anfrage an die
	// Verwaltung eine Aufforderung.
	[TNotifications.REQUEST_RECEIVED]: 'success',
	[TNotifications.REQUEST_NEW]: 'warning',

	// Eine Antwort ist eine Nachricht, eine Rueckfrage eine Aufforderung, ein
	// Abschluss eine Zusage — und was liegen bleibt, ist eine Mahnung.
	[TNotifications.REQUEST_ANSWERED]: 'info',
	[TNotifications.REQUEST_QUESTION]: 'warning',
	[TNotifications.REQUEST_CLOSED]: 'success',
	[TNotifications.REQUEST_REJECTED]: 'info',
	[TNotifications.REQUEST_REPLY]: 'info',
	[TNotifications.REQUEST_OVERDUE]: 'warning',

	/*
	 * Konzept. Diese vier Eintraege hatten bis 25.08.2026 keine Wirkung: Der
	 * Schluessel war `undefined`, weil die Arten in `shared/const` fehlten — und
	 * vier Eintraege mit demselben Schluessel sind ein Eintrag.
	 */
	[TNotifications.CONCEPT_SUBMITTED]: 'info',
	[TNotifications.CONCEPT_RETURNED]: 'warning',
	[TNotifications.CONCEPT_SCORED]: 'success',
	[TNotifications.CONCEPT_ASSESSMENT_REMINDER]: 'warning',

	// Zusage einer Expert:in: gute Nachricht; die Rückfrage verlangt eine Antwort.
	[TNotifications.SUPPORTER_REQUEST_ACCEPTED]: 'success',
	[TNotifications.SUPPORTER_REQUEST_ACCEPTED_TEACHER]: 'success',
	[TNotifications.SUPPORTER_CONTACT_CHECK]: 'warning',

	// Ein Abschluss ist eine gute Nachricht — fuer beide Seiten.
	[TNotifications.COURSE_COMPLETED]: 'success',
	[TNotifications.COURSE_COMPLETED_TEACHER]: 'success',

	// Kurse mit Urkunde: Abschluss und erreichte Obergrenze sind Zusagen.
	[TNotifications.LEARNING_COURSE_COMPLETED]: 'success',
	[TNotifications.LEARNING_COURSE_COMPLETED_TEACHER]: 'success',
	[TNotifications.LEARNING_COURSE_CREDIT_CAP_REACHED]: 'success'
}

/*
 * Hat diese Art überhaupt einen Text? Der Zweig `default` liefert null — für
 * abgeschaltete Arten (etwa USERS_JOINING) stand hier früher eine leere Karte
 * mit einem Datum darin. Die Liste fragt vorher, damit sie auch keine
 * Tagesüberschrift setzt, unter der nichts steht.
 *
 * Geprüft wird mit Platzhaltern: Ein Fehler bedeutet, dass der Zweig existiert
 * und nur die Platzhalter nicht mag — dann wird die Mitteilung gezeigt.
 */
export const hasNotificationLayout = type => {
	try {
		return getLayoutNotifications(type, {}, {}, '', {}, new Date().toISOString()) !== null
	} catch {
		return true
	}
}

function NotificationItem(props) {
	const { createdAt, isViewed, type } = props.notification
	const layout = getLayoutNotifications(
		type,
		props.notification.info,
		props.options?.reportsDeadline,
		props.company.type,
		props.options?.invoicesOverdue,
		createdAt,
		props.company.isActivated,
		props.company.isNeedReview,
		props.updateCompanyInfo,
		props.activationsStatus,
		props.options
	)

	// Eine unbekannte Art hat keinen Text — dann bleibt sonst eine leere Karte
	// mit einem Datum darin stehen.
	if (!layout) return null

	return (
		<article
			className={clsx(
				styles.notificationsItem,
				styles[TONES[type] || 'info'],
				props.compact && styles.compact,
				isViewed === false && styles.unread
			)}
		>
			{layout}
			<div className={styles.meta}>
				{isViewed === false && <span className={styles.badgeNew}>{i18n.t('New')}</span>}
				<time className={styles.metaTime} dateTime={createdAt}>
					{formatVienna(createdAt, props.compact ? 'dd.MM.yyyy' : 'HH:mm')}
				</time>
				{!props.compact && <span className={styles.metaDate}>{formatVienna(createdAt, 'dd.MM.yyyy')}</span>}
			</div>
		</article>
	)
}

NotificationItem.propTypes = {
	notification: PropTypes.shape({
		createdAt: PropTypes.string.isRequired,
		info: PropTypes.object,
		type: PropTypes.string.isRequired,
		isViewed: PropTypes.bool
	}).isRequired,
	compact: PropTypes.bool,
	updateCompanyInfo: PropTypes.func.isRequired,
	activationsStatus: PropTypes.shape({
		riskAnalysis: PropTypes.bool,
		employees: PropTypes.bool,
		experts: PropTypes.bool,
		parentalConfirmation: PropTypes.bool,
		isInvitedEmployeesCount: PropTypes.bool,
		isWorkAreaAssigned: PropTypes.bool,
		emailVerified: PropTypes.bool
	}).isRequired
}

const mapStateToProps = state => ({
	options: state.options,
	company: {
		type: state.user.profile.EmploymentCompany?.companyType || '',
		isActivated: !!state.user.profile.EmploymentCompany?.isActive,
		isNeedReview: !!state.user.profile.EmploymentCompany?.isNeedReview,
		emailVerified: !!state.user.profile.EmploymentCompany?.emailVerified
	}
})

const mapDispatchToProps = dispatch => ({
	updateCompanyInfo: data => dispatch(updateCompanyInfo(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(NotificationItem)

const downoload = (hashName, downloadName, afterDownload) => {
	http.get(TApi.STUDENT_DOWNLOAD_DOCUMENT(hashName), {
		headers: {
			'Content-Type': 'application/pdf'
		},
		responseType: 'blob'
	}).then(({ data }) => {
		const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
		const link = document.createElement('a')
		link.href = url
		link.setAttribute('download', downloadName)
		document.body.appendChild(link)
		link.click()
		link.parentNode.removeChild(link)
		if (afterDownload) afterDownload()
	})
}
const downoloadTeacher = (hashName, downloadName) => {
	http.get(TApi.TEACHER_DOWNLOAD_DOCUMENT(hashName), {
		headers: {
			'Content-Type': 'application/pdf'
		},
		responseType: 'blob'
	}).then(({ data }) => {
		const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
		const link = document.createElement('a')
		link.href = url
		link.setAttribute('download', downloadName)
		document.body.appendChild(link)
		link.click()
		link.parentNode.removeChild(link)
	})
}
